import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Menu from '../menu/Menu';
import Header from '../cabecalho/Cabecalho';
import Rodape from '../rodape/Rodape';
import Titulo from '../titulo/Titulo';
import ModalConfirmacao from '../modal/ModalConfirmacao';
import ModalChamado from './ModalChamado';
import ChamadosService from '../../service/chamados/ChamadosService';
import Snackbar from '../common/Snackbar';

// ─── Status config ──────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  Aberto:       { color: '#1a73e8', bg: '#e8f0fe', dot: '#1a73e8' },
  'Em andamento': { color: '#f59e0b', bg: '#fef9c3', dot: '#f59e0b' },
  Concluído:    { color: '#16a34a', bg: '#dcfce7', dot: '#16a34a' },
  Cancelado:    { color: '#dc2626', bg: '#fee2e2', dot: '#dc2626' },
};

const BORDER_COLOR = {
  Aberto:       '#1a73e8',
  'Em andamento': '#f59e0b',
  Concluído:    '#16a34a',
  Cancelado:    '#dc2626',
};

// ─── Styled Components ───────────────────────────────────────────────────────
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  padding-top: clamp(0.375rem, 0.8vh, 0.75rem);
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  min-height: 0;
  margin-top: 85px;
  margin-bottom: 50px;
`;

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-left: 255px;
  padding: clamp(1rem, 2vh, 1.5rem) clamp(1rem, 2.5vw, 2rem);
  box-sizing: border-box;

  @media (max-width: 1200px) {
    margin-left: 60px;
    padding-left: 20px;
  }

  @media (max-width: 767.98px) {
    margin-left: 0;
    padding: clamp(0.75rem, 3vw, 1.25rem);
  }
`;

const Content = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
  font-family: 'Roboto', sans-serif;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    margin: 0;
    max-width: none;
  }
`;

// Tabs row
const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 6px;
  border: 2px solid ${(p) => (p.$active ? '#008080' : '#b2d8d8')};
  background: ${(p) => (p.$active ? '#008080' : 'white')};
  color: ${(p) => (p.$active ? 'white' : '#008080')};
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &::after {
    content: '';
    display: ${(p) => (p.$active ? 'block' : 'none')};
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: 3px;
    background: #008080;
    border-radius: 2px;
  }
`;

const Badge = styled.span`
  background: ${(p) => (p.$active ? 'rgba(255,255,255,0.25)' : '#008080')};
  color: ${(p) => (p.$active ? 'white' : 'white')};
  border-radius: 12px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 600;
`;

// Search + actions row
const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1.5px solid #ccc;
  border-radius: 8px;
  padding: 6px 14px;
  background: white;
  gap: 8px;
  flex: 1;
  max-width: 320px;

  @media (max-width: 500px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  width: 100%;
  color: #333;
  background: transparent;

  &::placeholder {
    color: #aaa;
  }
`;

const ButtonsGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 500px) {
    justify-content: flex-end;
  }
`;

const BtnNovo = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #008080;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #006666; }
`;

const BtnCancelar = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #b91c1c; }
`;

// Subtítulo da aba
const AbaSubtitulo = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  color: #8e8e93;
  margin: 0 0 14px 0;
`;

// Cards list
const CardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// Card
const CardWrapper = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  display: flex;
  align-items: stretch;
  overflow: hidden;
  position: relative;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 14px rgba(0,0,0,0.12);
  }
`;

const CardBorderRight = styled.div`
  width: 6px;
  background: ${(p) => p.$color || '#ccc'};
  flex-shrink: 0;
  border-radius: 0 10px 10px 0;
  align-self: stretch;
`;

const CardCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  accent-color: #008080;
  cursor: pointer;
  flex-shrink: 0;
  margin: auto 12px auto 16px;

  @media (max-width: 500px) {
    margin: auto 8px auto 10px;
  }
`;

const CardIllustration = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  flex-shrink: 0;

  @media (max-width: 500px) {
    display: none;
  }
`;

const CardBody = styled.div`
  flex: 1;
  padding: 14px 12px 14px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const CardTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const CardId = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: #8e8e93;
`;

const CardAssignee = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const AssigneeAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(p) => p.$bg || '#008080'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  font-family: 'Roboto', sans-serif;
  flex-shrink: 0;
`;

const AssigneeName = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: #555;
  white-space: nowrap;
`;

const CardTitle = styled.h3`
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin: 2px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardDesc = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  color: #555;
  margin: 0 0 6px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardTagsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TagCategoria = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e0f2f1;
  color: #008080;
  border-radius: 14px;
  padding: 5px 14px;
  font-size: 13px;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
`;

const TagStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${(p) => STATUS_CONFIG[p.$status]?.bg || '#f0f0f0'};
  color: ${(p) => STATUS_CONFIG[p.$status]?.color || '#555'};
  border-radius: 14px;
  padding: 3px 10px;
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  user-select: none;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => STATUS_CONFIG[p.$status]?.dot || '#aaa'};
  flex-shrink: 0;
`;

const TagData = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #e8f0fe;
  color: #555;
  border-radius: 14px;
  padding: 5px 14px;
  font-size: 13px;
  font-family: 'Roboto', sans-serif;
`;

const CardEditBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  padding: 4px;
  display: flex;
  align-items: center;
  margin-left: auto;
  align-self: flex-end;
  margin-top: 4px;

  &:hover { color: #008080; }
`;

const CardDeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #dc2626;
  padding: 4px;
  display: flex;
  align-items: center;
  align-self: center;
  margin-right: 8px;

  &:hover { color: #b91c1c; }
`;

// Status dropdown
const StatusDropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const TagStatusClickable = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${(p) => STATUS_CONFIG[p.$status]?.color || '#aaa'};
  color: white;
  border-radius: 14px;
  padding: 5px 10px 5px 14px;
  font-size: 13px;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
`;

const StatusDropdown = styled.div`
  position: fixed;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.18);
  z-index: 9999;
  min-width: 160px;
  overflow: hidden;
  padding: 4px 0;
`;

const StatusOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: ${(p) => (p.$current ? '600' : '400')};
  color: ${(p) => STATUS_CONFIG[p.$status]?.color || '#333'};
  cursor: pointer;
  background: ${(p) => (p.$current ? STATUS_CONFIG[p.$status]?.bg || '#f5f5f5' : 'transparent')};
  text-align: left;

  &:hover {
    background: ${(p) => STATUS_CONFIG[p.$status]?.bg || '#f5f5f5'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #aaa;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  padding: 40px 0;
`;

const CodigoLink = styled.button`
  background: #e0f2f1;
  color: #005f5f;
  border: none;
  border-radius: 4px;
  padding: 1px 6px;
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  margin-right: 5px;
  vertical-align: middle;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover { background: #b2dfdb; }
`;

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_ENVIADOS = [
  {
    id: 'CHM-0042',
    titulo: 'Carrinho nº 14 com roda travada',
    descricao: 'A roda traseira direita está completamente travada, impossibilitando o uso do carrinho.',
    categoria: 'Manutenção de carrinhos',
    status: 'Em andamento',
    data: '01/06/2026',
    atribuidoA: 'Mário P.',
    atribuidoAvatarBg: '#008080',
  },
  {
    id: 'CHM-0039',
    titulo: 'Carrinho nº 07 com alça quebrada',
    descricao: 'A alça superior do carrinho apresenta rachadura e pode causar acidentes.',
    categoria: 'Manutenção de carrinhos',
    status: 'Aberto',
    data: '30/05/2026',
    atribuidoA: null,
    atribuidoAvatarBg: null,
  },
];

const MOCK_RECEBIDOS = [
  {
    id: 'CHM-0043',
    titulo: 'Carrinho nº 12 com roda desalinhada',
    descricao: 'A roda dianteira esquerda apresenta desalinhamento.',
    categoria: 'Manutenção de carrinhos',
    status: 'Aberto',
    data: '01/06/2026',
    atribuidoA: 'João Silva (eu)',
    atribuidoAvatarBg: '#1a73e8',
  },
  {
    id: 'CHM-0044',
    titulo: 'Carrinho nº 31 com parafuso de fixação solto',
    descricao: 'Parafuso de fixação solto na estrutura lateral do carrinho.',
    categoria: 'Manutenção de carrinhos',
    status: 'Em andamento',
    data: '30/05/2026',
    atribuidoA: 'João Silva (eu)',
    atribuidoAvatarBg: '#1a73e8',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name) {
  if (!name) return '?';
  const parts = name.replace(' (eu)', '').trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].substring(0, 2).toUpperCase();
}
function formatarData(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const AVATAR_COLORS = ['#008080', '#1a73e8', '#f59e0b', '#16a34a', '#7c3aed', '#0ea5e9'];
function colorFromId(id) {
  if (!id) return '#008080';
  let h = 0;
  for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function mapearChamado(c) {
  return {
    _id: c._id,
    id: c.numero,
    titulo: c.titulo,
    descricao: c.descricao,
    codigo: c.codigo || null,
    categoria: c.categoria,
    status: c.status,
    data: formatarData(c.createdAt),
    atribuidoA: c.atribuidoA?.nome || null,
    atribuidoAId: c.atribuidoA?._id || null,
    atribuidoAvatarBg: c.atribuidoA ? colorFromId(c.atribuidoA._id) : null,
  };
}
// ─── Display rack / book stand illustration ──────────────────────────────────
const CartIllustration = () => (
  <svg width="60" height="60" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="24" y="5" width="40" height="20" rx="2.5" fill="#7a4c7f" stroke="#1b1b1b" strokeWidth="3"/>
    <rect x="29" y="8" width="30" height="14" rx="2" fill="#dce8f8"/>
    <path d="M31 14H53" stroke="#8b6d8d" strokeWidth="2.4" strokeLinecap="round"/>
    <path d="M31 18H53" stroke="#8b6d8d" strokeWidth="2.4" strokeLinecap="round"/>

    <rect x="16" y="25" width="58" height="12" rx="3" fill="#1b1b1b"/>
    <rect x="19" y="27" width="52" height="8" rx="2" fill="#f3f3f3"/>
    <rect x="22" y="29" width="14" height="4" rx="1" fill="#d8d8d8"/>
    <rect x="38" y="29" width="18" height="4" rx="1" fill="#c6d4ef"/>

    <rect x="18" y="38" width="54" height="12" rx="3" fill="#1b1b1b"/>
    <rect x="21" y="40" width="22" height="8" rx="1.5" fill="#d6ebda"/>
    <rect x="45" y="40" width="23" height="8" rx="1.5" fill="#eadab5"/>

    <rect x="18" y="50" width="54" height="12" rx="3" fill="#1b1b1b"/>
    <rect x="21" y="52" width="24" height="8" rx="1.5" fill="#f3f3f3"/>
    <rect x="47" y="52" width="20" height="8" rx="1.5" fill="#dedfe1"/>

    <path d="M17 64H73" stroke="#1b1b1b" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="25" cy="70" r="5.5" fill="#1b1b1b"/>
    <circle cx="65" cy="70" r="5.5" fill="#1b1b1b"/>
  </svg>
);

// ─── Single Card component ────────────────────────────────────────────────────
const ChamadoCard = ({
  chamado,
  modoSelecao,
  selecionado,
  onToggleSelecao,
  podeAlterarStatus,
  onAlterarStatus,
  onEditar,
  onDeletar,
  onBuscaPorCodigo,
  onAbrir,
}) => {
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const statusList = ['Concluído', 'Em andamento', 'Aberto'];

  const handleStatusClick = (e) => {
    e.stopPropagation();
    if (!podeAlterarStatus) return;
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setDropdownAberto((v) => !v);
  };

  const handleSelectStatus = (event, novoStatus) => {
    event?.stopPropagation();
    event?.preventDefault();
    setDropdownAberto(false);
    onAlterarStatus && onAlterarStatus(chamado._id, novoStatus);
  };

  useEffect(() => {
    if (!dropdownAberto) return;
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownAberto(false);
      }
    };
    document.addEventListener('click', handleOutside);
    return () => document.removeEventListener('click', handleOutside);
  }, [dropdownAberto]);

  return (
    <CardWrapper
      onClick={() => {
        if (!modoSelecao && onAbrir) onAbrir(chamado._id);
      }}
      role={!modoSelecao && onAbrir ? 'button' : undefined}
      tabIndex={!modoSelecao && onAbrir ? 0 : undefined}
      onKeyDown={(e) => {
        if (!modoSelecao && onAbrir && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onAbrir(chamado._id);
        }
      }}
      style={!modoSelecao && onAbrir ? { cursor: 'pointer' } : undefined}
    >
      {modoSelecao && onDeletar ? (
        <CardDeleteBtn onClick={(e) => { e.stopPropagation(); onDeletar(chamado.id); }} title="Excluir">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
            <path d="M10 11v6M14 11v6"/>
            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
          </svg>
        </CardDeleteBtn>
      ) : (
        <CardCheckbox
          checked={selecionado}
          onChange={() => onToggleSelecao && onToggleSelecao(chamado.id)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <CardIllustration>
        <CartIllustration />
      </CardIllustration>
      <CardBody>
        <CardTopRow>
          <CardId>{chamado.id}</CardId>
          <CardAssignee>
            {chamado.atribuidoA ? (
              <>
                <AssigneeAvatar $bg={chamado.atribuidoAvatarBg}>
                  {getInitials(chamado.atribuidoA)}
                </AssigneeAvatar>
                <AssigneeName>
                  {chamado.status === 'Cancelado' ? chamado.atribuidoA : `${chamado.atribuidoA}`}
                </AssigneeName>
              </>
            ) : (
              <AssigneeName style={{ color: '#aaa' }}>Não atribuído</AssigneeName>
            )}
          </CardAssignee>
        </CardTopRow>
        <CardTitle>
          {chamado.codigo && (
            <CodigoLink
              onClick={(e) => { e.stopPropagation(); onBuscaPorCodigo && onBuscaPorCodigo(chamado.codigo); }}
              title={`Filtrar por ${chamado.codigo}`}
            >
              {chamado.codigo}
            </CodigoLink>
          )}
          {chamado.codigo
            ? chamado.titulo.replace(chamado.codigo, '').trim()
            : chamado.titulo}
        </CardTitle>
        <CardDesc>{chamado.descricao}</CardDesc>
        <CardTagsRow>
          <TagCategoria>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.61L23 6H6"/>
            </svg>
            {chamado.categoria}
          </TagCategoria>

          <StatusDropdownWrapper ref={wrapperRef}>
            <TagStatusClickable $status={chamado.status} onClick={handleStatusClick}>
              {chamado.status === 'Concluído' ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : chamado.status === 'Cancelado' ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              ) : (
                <StatusDot $status={chamado.status} style={{ background: 'rgba(255,255,255,0.85)' }} />
              )}
              {chamado.status}
              {podeAlterarStatus && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {dropdownAberto
                    ? <polyline points="18 15 12 9 6 15"/>
                    : <polyline points="6 9 12 15 18 9"/>}
                </svg>
              )}
            </TagStatusClickable>
            {dropdownAberto && (
              <StatusDropdown style={{ top: dropdownPos.top, left: dropdownPos.left }}>
                {statusList.map((s) => (
                  <StatusOption
                    key={s}
                    $status={s}
                    $current={s === chamado.status}
                    onClick={(event) => handleSelectStatus(event, s)}
                  >
                    {s === 'Concluído' ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      <StatusDot $status={s} />
                    )}
                    {s}
                  </StatusOption>
                ))}
              </StatusDropdown>
            )}
          </StatusDropdownWrapper>

          <TagData>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {chamado.data}
          </TagData>

          <CardEditBtn onClick={(e) => { e.stopPropagation(); onEditar && onEditar(chamado.id); }} title="Editar">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </CardEditBtn>
        </CardTagsRow>
      </CardBody>
      <CardBorderRight $color={BORDER_COLOR[chamado.status]} />
    </CardWrapper>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const Chamados = () => {
  const navigate = useNavigate();
  const [abaAtiva, setAbaAtiva] = useState('enviados');
  const [busca, setBusca] = useState('');
  const [modoSelecao, setModoSelecao] = useState(false);
  const [selecionados, setSelecionados] = useState([]);
  const [modalCancelar, setModalCancelar] = useState(false);
  const [modalDeletar, setModalDeletar] = useState(false);
  const [idAcao, setIdAcao] = useState(null);
  const [modalFormAberto, setModalFormAberto] = useState(false);
  const [chamadoEditando, setChamadoEditando] = useState(null);

  const [enviados, setEnviados] = useState([]);
  const [recebidos, setRecebidos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [snackbar, setSnackbar] = useState({ aberto: false, mensagem: '', tipo: 'success' });

  const mostrarSnackbar = (mensagem, tipo = 'success') => {
    setSnackbar({ aberto: true, mensagem, tipo });
  };

  const listaAtual = abaAtiva === 'enviados' ? enviados : recebidos;

  const carregarChamados = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [env, rec] = await Promise.all([
        ChamadosService.listar('enviados'),
        ChamadosService.listar('recebidos'),
      ]);
      setEnviados(env.map(mapearChamado));
      setRecebidos(rec.map(mapearChamado));
    } catch (e) {
      setErro('Nenhum chamado encontrado.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregarChamados(); }, [carregarChamados]);

  const listaFiltrada = listaAtual.filter((c) =>
    busca.trim() === '' ||
    c.titulo.toLowerCase().includes(busca.toLowerCase()) ||
    c.id.toLowerCase().includes(busca.toLowerCase()) ||
    (c.codigo && c.codigo.toLowerCase().includes(busca.toLowerCase()))
  );

  const handleToggleSelecao = (id) => {
    setSelecionados((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      setModoSelecao(next.length > 0);
      return next;
    });
  };

  const handleCancelarModo = () => {
    setModoSelecao(false);
    setSelecionados([]);
  };

  // Enviados: cancelar chamado selecionado
  const handleCancelarChamado = () => {
    if (selecionados.length === 0) return;
    const sel = selecionados[0];
    const chamado = enviados.find((c) => c.id === sel || c._id === sel);

    if (chamado?.status === 'Cancelado') {
      setIdAcao(chamado?._id || sel);
      setModalDeletar(true);
      return;
    }

    setIdAcao(chamado?._id || sel);
    setModalCancelar(true);
  };

  const confirmarCancelamento = async () => {
    try {
      await ChamadosService.atualizarStatus(idAcao, 'Cancelado');
      await carregarChamados();
      mostrarSnackbar('Chamado cancelado com sucesso.');
    } catch (e) {
      console.error('Erro ao cancelar chamado:', e);
      mostrarSnackbar('Erro ao cancelar chamado. Tente novamente.', 'error');
    } finally {
      setModalCancelar(false);
      setSelecionados([]);
      setModoSelecao(false);
    }
  };

  // Recebidos: deletar chamado
  const handleDeletar = (_id) => {
    setIdAcao(_id);
    setModalDeletar(true);
  };

  const confirmarDelecao = async () => {
    try {
      await ChamadosService.excluir(idAcao);
      await carregarChamados();
      mostrarSnackbar('Chamado excluído com sucesso.');
    } catch (e) {
      console.error('Erro ao excluir chamado:', e);
      mostrarSnackbar('Erro ao excluir chamado. Tente novamente.', 'error');
    } finally {
      setModalDeletar(false);
      setIdAcao(null);
      setSelecionados([]);
      setModoSelecao(false);
    }
  };

  const handleAlterarStatus = async (id, novoStatus) => {
    try {
      await ChamadosService.atualizarStatus(id, novoStatus);
      await carregarChamados();
      mostrarSnackbar(`Status atualizado para “${novoStatus}”.`);
    } catch (e) {
      console.error('Erro ao alterar status:', e);
      mostrarSnackbar('Erro ao atualizar status. Tente novamente.', 'error');
    }
  };

  const handleAbrirNovoChamado = () => {
    setChamadoEditando(null);
    setModalFormAberto(true);
  };

  const handleAbrirEdicao = (id) => {
    const lista = abaAtiva === 'enviados' ? enviados : recebidos;
    const chamado = lista.find((c) => c.id === id) || null;
    setChamadoEditando(chamado);
    setModalFormAberto(true);
  };

  const handleAbrirDetalhe = (id) => {
    if (!id) return;
    navigate(`/chamados/${id}`);
  };

  const handleSalvarChamado = async ({ titulo, atribuidoAId, descricao, codigo }) => {
    try {
      if (chamadoEditando) {
        await ChamadosService.editar(chamadoEditando._id, {
          titulo,
          descricao,
          atribuidoAId: atribuidoAId !== undefined ? atribuidoAId : undefined,
        });
        mostrarSnackbar('Chamado atualizado com sucesso.');
      } else {
        await ChamadosService.criar({
          titulo,
          descricao,
          codigo,
          categoria: 'Manutenção de carrinhos',
          atribuidoAId: atribuidoAId || null,
        });
        setAbaAtiva('enviados');
        mostrarSnackbar('Chamado criado com sucesso!');
      }
      await carregarChamados();
    } catch (e) {
      console.error('Erro ao salvar chamado:', e);
      mostrarSnackbar('Erro ao salvar chamado. Tente novamente.', 'error');
    }
  };

  return (
    <>
      <PageWrapper>
        <Header />
        <MainContentWrapper>
          <Menu />
          <Container>
            <Content>
              <Titulo>Chamados</Titulo>
              <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: 13, color: '#8e8e93', margin: '0 0 16px 0' }}>
                Gerencie e acompanhe os chamados criados por você ou atribuídos a você para atendimento.
              </p>

              {/* Tabs + search + actions */}
              <TabsRow>
                <Tab $active={abaAtiva === 'enviados'} onClick={() => { setAbaAtiva('enviados'); handleCancelarModo(); }}>
                  Enviados <Badge $active={abaAtiva === 'enviados'}>{enviados.length}</Badge>
                </Tab>
                <Tab $active={abaAtiva === 'recebidos'} onClick={() => { setAbaAtiva('recebidos'); handleCancelarModo(); }}>
                  Recebidos <Badge $active={abaAtiva === 'recebidos'}>{recebidos.length}</Badge>
                </Tab>
              </TabsRow>

              <ActionsRow>
                <AbaSubtitulo style={{ margin: 0, flex: 1 }}>
                  {abaAtiva === 'enviados'
                    ? 'Chamados que você criou e está acompanhando.'
                    : 'Chamados atribuídos a você para atendimento.'}
                </AbaSubtitulo>
                <SearchWrapper>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <SearchInput
                    placeholder="Pesquisar"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </SearchWrapper>
                <ButtonsGroup>
                  {abaAtiva === 'enviados' && !modoSelecao && (
                    <BtnNovo onClick={handleAbrirNovoChamado}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      Novo Chamado
                    </BtnNovo>
                  )}
                  {modoSelecao && abaAtiva === 'enviados' && (
                    <BtnCancelar
                      onClick={handleCancelarChamado}
                      disabled={selecionados.length === 0}
                      style={{ opacity: selecionados.length === 0 ? 0.5 : 1 }}
                    >
                      {(() => {
                        const sel = selecionados[0];
                        const chamado = enviados.find((c) => c.id === sel || c._id === sel);
                        return chamado?.status === 'Cancelado' ? 'Apagar Chamado' : 'Cancelar Chamado';
                      })()}
                    </BtnCancelar>
                  )}
                </ButtonsGroup>
              </ActionsRow>

              {/* Cards */}
              <CardsList>
                {carregando ? (
                  <EmptyState>Carregando chamados...</EmptyState>
                ) : erro ? (
                  <EmptyState>{erro}</EmptyState>
                ) : listaFiltrada.length === 0 ? (
                  <EmptyState>Nenhum chamado encontrado.</EmptyState>
                ) : (
                  listaFiltrada.map((chamado) => (
                    <ChamadoCard
                      key={chamado._id || chamado.id}
                      chamado={chamado}
                      modoSelecao={modoSelecao}
                      selecionado={selecionados.includes(chamado.id)}
                      onToggleSelecao={handleToggleSelecao}
                      podeAlterarStatus={true}
                      onAlterarStatus={handleAlterarStatus}
                      onEditar={handleAbrirEdicao}
                      onDeletar={abaAtiva === 'recebidos' && modoSelecao ? () => handleDeletar(chamado._id) : null}
                      onBuscaPorCodigo={(codigo) => setBusca(codigo)}
                      onAbrir={handleAbrirDetalhe}
                    />
                  ))
                )}
              </CardsList>
            </Content>
          </Container>
        </MainContentWrapper>
        <Rodape />
      </PageWrapper>

      {snackbar.aberto && (
        <Snackbar
          mensagem={snackbar.mensagem}
          tipo={snackbar.tipo}
          onClose={() => setSnackbar((s) => ({ ...s, aberto: false }))}
        />
      )}

      {/* Modal cancelar chamado */}
      <ModalConfirmacao
        isOpen={modalCancelar}
        mensagem="Deseja confirmar o cancelamento desse chamado?"
        onConfirm={confirmarCancelamento}
        onClose={() => setModalCancelar(false)}
      />

      {/* Modal deletar chamado */}
      <ModalConfirmacao
        isOpen={modalDeletar}
        mensagem="Deseja confirmar a exclusão desse chamado?"
        onConfirm={confirmarDelecao}
        onClose={() => { setModalDeletar(false); setIdAcao(null); }}
      />

      {/* Modal novo/editar chamado */}
      {modalFormAberto && (
        <ModalChamado
          chamado={chamadoEditando}
          onClose={() => { setModalFormAberto(false); setChamadoEditando(null); }}
          onSalvar={handleSalvarChamado}
        />
      )}
    </>
  );
};

export default Chamados;
